import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, ErrorApi } from '../../api/cliente';
import type { EventoAdmin, EventoEntrada, TipoEvento } from '../../api/tipos';
import { DialogoConfirmacion } from '../../componentes/DialogoConfirmacion';
import { EsqueletoFormulario } from '../../componentes/Esqueletos';
import { Aviso } from '../../componentes/Estados';
import { Resultados } from '../../componentes/Resultados';
import { fechaLarga } from '../../lib/fechas';
import { HORA_CIERRE_POR_DEFECTO } from './constantes';
import { EditorPreguntas, type PreguntaEditable } from './EditorPreguntas';
import { exportarExcel, exportarPdf } from './exportarRespuestas';
import { TablaRespuestas } from './TablaRespuestas';

type Solapa = 'detalles' | 'campos' | 'resultados';

interface Estado {
  titulo: string;
  tipoEventoId: string;
  lugar: string;
  fecha: string;
  hora: string;
  cierre: string;
  cierreHora: string;
  descripcion: string;
  preguntas: PreguntaEditable[];
}

const estadoVacio = (): Estado => ({
  titulo: '',
  tipoEventoId: '',
  lugar: '',
  fecha: '',
  hora: '19:00',
  cierre: '',
  cierreHora: HORA_CIERRE_POR_DEFECTO,
  descripcion: '',
  preguntas: [],
});

function estadoDesde(evento: EventoAdmin): Estado {
  return {
    titulo: evento.titulo,
    tipoEventoId: evento.tipoEventoId ?? '',
    lugar: evento.lugar ?? '',
    fecha: evento.fechaLocal,
    hora: evento.horaLocal,
    cierre: evento.cierreLocal ?? '',
    cierreHora: evento.cierreHoraLocal ?? HORA_CIERRE_POR_DEFECTO,
    descripcion: evento.descripcion ?? '',
    preguntas: evento.preguntas.map((pregunta) => ({
      id: pregunta.id,
      claveLocal: pregunta.id,
      etiqueta: pregunta.etiqueta,
      tipo: pregunta.tipo,
      condicional: pregunta.condicional,
      privada: pregunta.privada,
      opciones: pregunta.opciones.map((opcion) => ({
        id: opcion.id,
        claveLocal: opcion.id,
        texto: opcion.texto,
      })),
      reiniciarVotos: false,
      votos: pregunta.votos ?? 0,
    })),
  };
}

const aCuerpo = (estado: Estado): EventoEntrada => ({
  titulo: estado.titulo.trim(),
  descripcion: estado.descripcion.trim() || null,
  lugar: estado.lugar.trim() || null,
  fecha: estado.fecha,
  hora: estado.hora,
  cierre: estado.cierre || null,
  cierreHora: estado.cierre ? estado.cierreHora || HORA_CIERRE_POR_DEFECTO : null,
  tipoEventoId: estado.tipoEventoId || null,
  preguntas: estado.preguntas.map((pregunta) => ({
    id: pregunta.id,
    etiqueta: pregunta.etiqueta.trim(),
    tipo: pregunta.tipo,
    condicional: pregunta.condicional,
    privada: pregunta.privada,
    opciones: pregunta.opciones.map((opcion) => ({ id: opcion.id, texto: opcion.texto.trim() })),
    reiniciarVotos: pregunta.reiniciarVotos,
  })),
});

const solapaClase = (activa: boolean) =>
  `border-b-2 px-4 py-2.5 font-titulo text-[13.5px] font-semibold transition-colors ${
    activa ? 'border-lima text-texto' : 'border-transparent text-debil hover:text-suave'
  }`;

interface Props {
  eventoId: string | null;
  tipos: TipoEvento[];
  onCancelar: () => void;
  onGuardado: (id: string) => void;
  onEliminado: () => void;
  /// Avisa si hay cambios sin guardar, para que la vista confirme antes de salir.
  onCambiosPendientes?: (hayCambios: boolean) => void;
}

export function FormularioEvento({
  eventoId,
  tipos,
  onCancelar,
  onGuardado,
  onEliminado,
  onCambiosPendientes,
}: Props) {
  const clienteConsultas = useQueryClient();
  const [solapa, setSolapa] = useState<Solapa>('detalles');
  const [estado, setEstado] = useState<Estado>(estadoVacio);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  // Un evento existente se abre en modo lectura; uno nuevo arranca editable.
  const [editando, setEditando] = useState(!eventoId);
  const [exportando, setExportando] = useState<'excel' | 'pdf' | null>(null);
  const [errorExportar, setErrorExportar] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<'guardar' | 'salir' | null>(null);

  const exportar = async (formato: 'excel' | 'pdf', datos: EventoAdmin) => {
    setExportando(formato);
    setErrorExportar(null);
    try {
      await (formato === 'pdf' ? exportarPdf(datos) : exportarExcel(datos));
    } catch (error) {
      console.error('Falló la exportación', error);
      setErrorExportar('No se pudo generar el archivo. Probá de nuevo.');
    } finally {
      setExportando(null);
    }
  };

  const evento = useQuery({
    queryKey: ['admin', 'evento', eventoId],
    queryFn: () => api.get<EventoAdmin>(`/admin/eventos/${eventoId!}`),
    enabled: Boolean(eventoId),
  });

  useEffect(() => {
    setSolapa('detalles');
    setConfirmandoBorrado(false);
    setEditando(!eventoId);
    if (!eventoId) setEstado(estadoVacio());
  }, [eventoId]);

  // Mientras se edita no se pisa el formulario si llega un refetch del evento.
  useEffect(() => {
    if (evento.data && !editando) setEstado(estadoDesde(evento.data));
  }, [evento.data, editando]);

  // Cambios sin guardar = el formulario difiere de lo guardado (o del formulario vacío).
  const referencia = useMemo(
    () => (eventoId ? (evento.data ? estadoDesde(evento.data) : null) : estadoVacio()),
    [eventoId, evento.data],
  );
  const hayCambios =
    editando && referencia !== null && JSON.stringify(estado) !== JSON.stringify(referencia);

  useEffect(() => {
    onCambiosPendientes?.(hayCambios);
  }, [hayCambios, onCambiosPendientes]);

  useEffect(() => () => onCambiosPendientes?.(false), [onCambiosPendientes]);

  // Con un pop-up abierto, Escape lo cierra sin hacer nada.
  useEffect(() => {
    if (!confirmando) return;
    const alEscapar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setConfirmando(null);
    };
    document.addEventListener('keydown', alEscapar);
    return () => document.removeEventListener('keydown', alEscapar);
  }, [confirmando]);

  const guardar = useMutation({
    mutationFn: (cuerpo: EventoEntrada) =>
      eventoId
        ? api.put<EventoAdmin>(`/admin/eventos/${eventoId}`, cuerpo)
        : api.post<EventoAdmin>('/admin/eventos', cuerpo),
    onSuccess: (guardado) => {
      clienteConsultas.setQueryData(['admin', 'evento', guardado.id], guardado);
      void clienteConsultas.invalidateQueries({ queryKey: ['admin', 'eventos'] });
      void clienteConsultas.invalidateQueries({ queryKey: ['eventos'] });
      setEditando(false);
      onGuardado(guardado.id);
    },
  });

  const eliminar = useMutation({
    mutationFn: () => api.borrar(`/admin/eventos/${eventoId!}`),
    onSuccess: () => {
      void clienteConsultas.invalidateQueries({ queryKey: ['admin', 'eventos'] });
      void clienteConsultas.invalidateQueries({ queryKey: ['eventos'] });
      onEliminado();
    },
  });

  if (eventoId && evento.isPending) return <EsqueletoFormulario />;

  const error =
    guardar.error instanceof ErrorApi
      ? guardar.error
      : eliminar.error instanceof ErrorApi
        ? eliminar.error
        : null;

  const hayRespuestas = (evento.data?.respondieron ?? 0) > 0;

  // En un evento existente, cancelar descarta los cambios y vuelve a modo lectura;
  // en uno nuevo, cierra el formulario.
  const cancelar = () => {
    setConfirmando(null);
    if (!eventoId) return onCancelar();
    if (evento.data) setEstado(estadoDesde(evento.data));
    setConfirmandoBorrado(false);
    guardar.reset();
    eliminar.reset();
    setEditando(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 border-b border-white/[.08] px-5 py-4 sm:px-6">
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-titulo text-[20px] font-extrabold">
            {eventoId ? estado.titulo || 'Evento' : 'Nuevo evento'}
          </h2>
          <p className="mt-0.5 text-xs text-debil">
            {eventoId
              ? `${evento.data?.respondieron ?? 0} respuestas · ${evento.data?.confirmados ?? 0} confirmados`
              : 'Se publica apenas lo guardes.'}
          </p>
        </div>

        {editando ? (
          <>
            <button
              type="button"
              onClick={() => (hayCambios ? setConfirmando('salir') : cancelar())}
              className="rounded-[9px] border border-white/15 px-4 py-2 text-[13px] text-tenue transition-colors hover:bg-white/5"
            >
              Cancelar
            </button>

            {eventoId && (
              <button
                type="button"
                onClick={() => setConfirmandoBorrado(true)}
                className="rounded-[9px] border border-coral/35 px-4 py-2 text-[13px] text-coral transition-colors hover:bg-coral/10"
              >
                Eliminar
              </button>
            )}

            <button
              type="button"
              disabled={guardar.isPending}
              // Crear un evento nuevo no pide confirmación; guardar cambios de uno existente, sí.
              onClick={() =>
                eventoId ? setConfirmando('guardar') : guardar.mutate(aCuerpo(estado))
              }
              className="rounded-[9px] bg-lima px-5 py-2 font-titulo text-[13.5px] font-bold text-tinta transition-colors hover:bg-lima-claro disabled:opacity-60"
            >
              {guardar.isPending ? 'Guardando…' : eventoId ? 'Guardar cambios' : 'Crear evento'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="rounded-[9px] bg-lima px-5 py-2 font-titulo text-[13.5px] font-bold text-tinta transition-colors hover:bg-lima-claro"
          >
            Editar
          </button>
        )}
      </div>

      <div className="flex gap-0.5 overflow-x-auto border-b border-white/[.08] px-5 pt-3 sm:px-6">
        <button type="button" className={solapaClase(solapa === 'detalles')} onClick={() => setSolapa('detalles')}>
          Detalles
        </button>
        <button type="button" className={solapaClase(solapa === 'campos')} onClick={() => setSolapa('campos')}>
          Campos de votación
        </button>
        {eventoId && (
          <button type="button" className={solapaClase(solapa === 'resultados')} onClick={() => setSolapa('resultados')}>
            Resultados en vivo
          </button>
        )}
      </div>

      {error && (
        <div className="px-5 pt-4 sm:px-6">
          <Aviso tono="error">{error.message}</Aviso>
        </div>
      )}

      {confirmandoBorrado && (
        <div className="px-5 pt-4 sm:px-6">
          <Aviso tono="error">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {hayRespuestas
                  ? `Se van a borrar también las ${evento.data?.respondieron} respuestas cargadas. No se puede deshacer.`
                  : 'Se va a eliminar el evento. No se puede deshacer.'}
              </span>
              <button
                type="button"
                disabled={eliminar.isPending}
                onClick={() => eliminar.mutate()}
                className="rounded-lg bg-coral px-3.5 py-1.5 text-xs font-bold text-tinta disabled:opacity-60"
              >
                {eliminar.isPending ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmandoBorrado(false)}
                className="rounded-lg border border-white/20 px-3.5 py-1.5 text-xs text-suave"
              >
                Cancelar
              </button>
            </div>
          </Aviso>
        </div>
      )}

      {solapa === 'detalles' && (
        <fieldset
          disabled={!editando}
          className="grid min-w-0 max-w-[860px] grid-cols-1 gap-4 p-5 sm:grid-cols-2 sm:gap-x-[18px] sm:p-6"
        >
          <div className="sm:col-span-2">
            <label className="etiqueta" htmlFor="titulo">Título del evento</label>
            <input
              id="titulo"
              type="text"
              className="campo"
              maxLength={200}
              placeholder="Ej: Cumpleaños de septiembre"
              value={estado.titulo}
              onChange={(e) => setEstado({ ...estado, titulo: e.target.value })}
            />
          </div>

          <div>
            <label className="etiqueta" htmlFor="tipo">Tipo de evento</label>
            <select
              id="tipo"
              className="campo"
              value={estado.tipoEventoId}
              onChange={(e) => setEstado({ ...estado, tipoEventoId: e.target.value })}
            >
              <option value="">Sin tipo</option>
              {tipos.map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </select>
            {tipos.length === 0 && (
              <p className="mt-1.5 text-xs text-mudo">
                Todavía no cargaste ningún tipo. Se crean desde «Tipos de evento».
              </p>
            )}
          </div>

          <div>
            <label className="etiqueta" htmlFor="lugar">Lugar</label>
            <input
              id="lugar"
              type="text"
              className="campo"
              maxLength={200}
              placeholder="Ej: Terraza — Oficina Córdoba"
              value={estado.lugar}
              onChange={(e) => setEstado({ ...estado, lugar: e.target.value })}
            />
          </div>

          <div>
            <span className="etiqueta">Fecha y hora</span>
            <div className="flex gap-2.5">
              <input
                type="date"
                className="campo flex-1"
                value={estado.fecha}
                onChange={(e) => setEstado({ ...estado, fecha: e.target.value })}
              />
              <input
                type="time"
                className="campo w-[118px] flex-none"
                value={estado.hora}
                onChange={(e) => setEstado({ ...estado, hora: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="etiqueta" htmlFor="cierre">Cierre de inscripción</label>
            <div className="flex gap-2.5">
              <input
                id="cierre"
                type="date"
                className="campo flex-1"
                value={estado.cierre}
                max={estado.fecha || undefined}
                onChange={(e) => setEstado({ ...estado, cierre: e.target.value })}
              />
              <input
                type="time"
                aria-label="Hora de cierre"
                className="campo w-[118px] flex-none disabled:opacity-40"
                disabled={!estado.cierre}
                value={estado.cierreHora}
                onChange={(e) =>
                  setEstado({ ...estado, cierreHora: e.target.value || HORA_CIERRE_POR_DEFECTO })
                }
              />
            </div>
            <p className="mt-1.5 text-xs text-mudo">
              {!estado.cierre
                ? 'Sin fecha: se puede responder hasta que ocurra el evento.'
                : estado.cierreHora === HORA_CIERRE_POR_DEFECTO
                  ? `Se puede responder durante todo el ${fechaLarga(estado.cierre)}.`
                  : `Se puede responder hasta las ${estado.cierreHora} del ${fechaLarga(estado.cierre)}.`}
            </p>
          </div>

          <div className="sm:col-span-2">
            <label className="etiqueta" htmlFor="descripcion">Descripción</label>
            <textarea
              id="descripcion"
              rows={3}
              className="campo resize-y leading-relaxed"
              maxLength={4000}
              placeholder="Qué se hace, qué llevar, cualquier detalle útil."
              value={estado.descripcion}
              onChange={(e) => setEstado({ ...estado, descripcion: e.target.value })}
            />
          </div>
        </fieldset>
      )}

      {solapa === 'campos' && (
        <fieldset disabled={!editando} className="min-w-0 p-5 sm:p-6">
          <EditorPreguntas
            preguntas={estado.preguntas}
            onCambio={(preguntas) => setEstado({ ...estado, preguntas })}
          />
        </fieldset>
      )}

      {solapa === 'resultados' && evento.data && (
        <div className="p-5 sm:p-6">
          <div className="mb-6 flex flex-wrap gap-3.5">
            {[
              { valor: evento.data.confirmados, etiqueta: 'Confirmados', color: '#A1C623' },
              { valor: evento.data.noAsisten, etiqueta: 'No asisten', color: '#9DB0C7' },
              { valor: evento.data.respondieron, etiqueta: 'Respondieron', color: '#29B0E6' },
            ].map((kpi) => (
              <div
                key={kpi.etiqueta}
                className="min-w-[132px] rounded-xl border border-white/[.09] bg-white/[.04] px-5 py-3.5"
              >
                <div
                  className="font-titulo text-[28px] font-extrabold leading-none"
                  style={{ color: kpi.color }}
                >
                  {kpi.valor}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[1.1px] text-debil">
                  {kpi.etiqueta}
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-[760px]">
            <Resultados resultados={evento.data.resultados} />
          </div>

          <div className="mt-8">
            <div className="mb-3 flex flex-wrap items-center gap-2.5">
              <div className="flex-1 text-[11px] uppercase tracking-[1.3px] text-debil">
                Respuestas por colaborador ({evento.data.respuestas.length})
              </div>
              {(['excel', 'pdf'] as const).map((formato) => (
                <button
                  key={formato}
                  type="button"
                  disabled={exportando !== null || evento.data.respuestas.length === 0}
                  onClick={() => void exportar(formato, evento.data)}
                  title={
                    formato === 'pdf'
                      ? 'Lista de confirmados con un cuadro para tildar a los que llegan'
                      : 'Todas las respuestas, en una planilla'
                  }
                  className="rounded-[9px] border border-white/15 px-3.5 py-1.5 text-[12.5px] font-semibold text-suave transition-colors hover:bg-white/5 disabled:cursor-default disabled:opacity-40"
                >
                  {exportando === formato
                    ? 'Generando…'
                    : formato === 'pdf'
                      ? 'Exportar PDF'
                      : 'Exportar Excel'}
                </button>
              ))}
            </div>
            {errorExportar && (
              <div className="mb-3">
                <Aviso tono="error">{errorExportar}</Aviso>
              </div>
            )}
            <TablaRespuestas
              preguntas={evento.data.preguntas}
              respuestas={evento.data.respuestas}
            />
          </div>
        </div>
      )}

      {confirmando === 'guardar' && (
        <DialogoConfirmacion
          titulo="¿Guardar los cambios?"
          textoConfirmar="Sí, guardar"
          onConfirmar={() => {
            setConfirmando(null);
            guardar.mutate(aCuerpo(estado));
          }}
          onCancelar={() => setConfirmando(null)}
        >
          {hayRespuestas
            ? `Los cambios se publican al instante. El evento ya tiene ${evento.data?.respondieron} respuestas.`
            : 'Los cambios se publican al instante.'}
        </DialogoConfirmacion>
      )}

      {confirmando === 'salir' && (
        <DialogoConfirmacion
          titulo="¿Salir sin guardar?"
          textoConfirmar="Salir sin guardar"
          textoCancelar="Seguir editando"
          tono="peligro"
          onConfirmar={cancelar}
          onCancelar={() => setConfirmando(null)}
        >
          Tenés cambios que todavía no guardaste. Si salís, se pierden.
        </DialogoConfirmacion>
      )}
    </div>
  );
}
